import React, { ReactNode, useEffect } from 'react';

interface ConfirmDialogProps {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * In-app replacement for window.confirm(). Uses the shared modal
 * classes plus `modal--confirm` so it stacks above a host modal
 * (z 110 > 100) — e.g. deleting a track from inside its edit modal.
 * Escape / backdrop click cancel; the destructive action never has
 * default focus.
 */
export default function ConfirmDialog({
  title, message, confirmLabel = 'Delete', onConfirm, onCancel,
}: ConfirmDialogProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      className="modal modal--confirm"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="modal__panel confirm-dialog-panel">
        <header className="modal__head">
          <h3 id="confirm-title">{title}</h3>
          <button type="button" className="modal__close" onClick={onCancel} aria-label="Cancel">×</button>
        </header>
        <div className="modal__body">{message}</div>
        <footer className="modal__foot">
          <button type="button" className="ghost btn--sm" onClick={onCancel} autoFocus>
            Cancel
          </button>
          <button type="button" className="danger btn--sm" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}
